<template>
  <Modal @cancel="close">
    <template v-slot:title>
      <p class="modal-card-title">Create a Workspace</p>
    </template>

    <template v-slot:body>
      <div class="field">
        <label for="workspace-modal-name-input" class="label">Name</label>
        <div class="control">
          <input
            id="workspace-modal-name-input"
            ref="nameInput"
            v-model="workspaceName"
            class="input"
            type="text"
            placeholder="Enter the name of the workspace..."
            @input="$v.workspaceName.$touch()"
            @keypress.enter="createWorkspace"
            :class="{ 'is-danger': $v.workspaceName.$invalid && $v.workspaceName.$dirty }"
          />
          <p class="help is-danger" v-if="$v.workspaceName.$invalid && $v.workspaceName.$dirty">
            {{ getErrorMessage() }}
          </p>
        </div>
      </div>
    </template>

    <template v-slot:buttons>
      <n-button-group>
        <n-button variant="primary" @click="createWorkspace" :disabled="$v.$invalid" :loading="isLoading">
          Create Workspace
        </n-button>
        <n-button @click="close" variant="primary--outline">Cancel</n-button>
      </n-button-group>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import Modal from '@/shared/components/Modal.vue';
import { FlowListItemViewModel } from '@/features/bots/models/flowListItemViewModel';
import useVuelidate, { ValidationRule } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import VuelidateUtil from '@/shared/util/vuelidateUtil';
import { useStore } from 'vuex';
import Toast from '@shared/services/toast';
import { workspacesApi } from '@/workspaces/workspaces';

const emit = defineEmits<{
  (e: 'workspace-created', flow: FlowListItemViewModel): void;
  (e: 'close'): void;
}>();

const store = useStore();

const workspaceName = ref('');
const isLoading = ref(false);
const nameInput = ref<HTMLInputElement | null>(null);

// Validation rules for the flowName
const validations = {
  workspaceName: {
    required,
    maxLength: maxLength(250),
    workspaceNameValid: VuelidateUtil.objectName,
  },
};

const $v = useVuelidate(validations, { workspaceName });

onMounted(() => {
  if (nameInput.value) {
    nameInput.value.focus();
  }
});

async function createWorkspace() {
  if ($v.value.$invalid) {
    return;
  }

  isLoading.value = true;
  try {
    const newWorkspace = await workspacesApi.createWorkspace({
      name: workspaceName.value,
      features: {},
      usageLimits: {},
    });
    isLoading.value = false;
    emit('workspace-created', newWorkspace);
    close();
  } catch (e: any) {
    isLoading.value = false;
    if (e.status === 409) {
      Toast.showError(e.message);
      return;
    }
    Toast.showError('Failed to create workspace');
  }
}

function getErrorMessage() {
  return VuelidateUtil.getErrorMessage($v, 'workspaceName', 'Name', (validationRule: ValidationRule) => {
    if (VuelidateUtil.isValidationInvalid($v, 'workspaceName', 'workspaceNameValid')) {
      return 'Name must only contain letters, numbers, spaces, and the characters , - _ ( ) &';
    }
    return '';
  });
}

function close() {
  emit('close');
}
</script>
